<script lang="ts">
  import * as H from "../settings/hints.ts";
  import * as R from "../settings/receipts.ts";
  import { fill, shows } from "../settings/index.ts";
  import { moneyCards } from "../lib/moneyCards.svelte.ts";
  import { RECEIPT_CHAT_PROMPT, draftSubtotal, nextReceiptLabel, pastedReceipts, receiptAddCommands, type ReceiptDraft } from "@split/core";
  import { app } from "../lib/store.svelte.ts";
  import CardPicker from "./CardPicker.svelte";
  import { onDestroy, onMount, untrack } from "svelte";
  import { photoToDataUrl, receiptScanner, type PendingScan } from "../lib/ai/scan.svelte.ts";
  import { centsInput, formatCents, readMoney, shortDate, tidyName, today } from "../lib/format.ts";

  interface Row {
    keep: boolean;
    itemNo: string;
    name: string;
    price: string;
    coupon: string;
    crv: string;
    taxed: "yes" | "no" | "";
  }

  let rows = $state<Row[]>([]);
  let store = $state("");
  let date = $state("");
  let receipt = $state("");
  let card = $state("");
  let total = $state("");
  let ticked = $state<string[]>([]);
  let saving = $state(false);
  let fileInput: HTMLInputElement | undefined = $state();
  /** The box for an AI chat's reply (when LM Studio isn't available), and what's in it. */
  let pasting = $state(false);
  let pasteText = $state("");
  let pasteError = $state("");

  const S = $derived(app.current);
  const people = $derived(Object.values(S.people).filter((p) => !p.archived));
  /**
   * Every receipt read in this group and waiting to be checked, in the order they were read.
   * The first is the one open below; adding or cancelling it opens the next.
   */
  const waiting = $derived(receiptScanner.pending.filter((p) => p.groupId === app.groupId));
  const current = $derived<PendingScan | null>(waiting[0] ?? null);
  const draft = $derived<ReceiptDraft | null>(current?.draft ?? null);
  const kept = $derived(rows.filter((r) => r.keep));
  const rowsCents = $derived(kept.reduce((s, r) => s + (readMoney(r.price) ?? 0) - Math.abs(readMoney(r.coupon) ?? 0) + Math.abs(readMoney(r.crv) ?? 0), 0));
  const printed = $derived(draft?.subtotalCents ?? null);

  function pick(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    const files = [...(input.files ?? [])];
    input.value = "";
    return scanFiles(files);
  }

  /**
   * Reads the photos one after another. Every receipt found in each goes on the waiting list in
   * order (one photo can hold several), so the first can be checked while the rest are read.
   */
  export async function scanFiles(files: Blob[]) {
    receiptScanner.begin();
    receiptScanner.stopped = false;
    if (!files.length) return receiptScanner.note("No photo was picked");
    // The group the scan belongs to, whatever page you're on when the AI finishes.
    const groupId = app.groupId;
    const failed: string[] = [];
    try {
      for (const [i, file] of files.entries()) {
        if (receiptScanner.stopped) {
          receiptScanner.note(`Stopped: ${files.length - i} photos left unread`);
          break;
        }
        receiptScanner.photo = { n: i + 1, of: files.length };
        receiptScanner.note(`Photo ${i + 1} of ${files.length}`);
        const drafts = await scanOne(file, groupId);
        if (!drafts && receiptScanner.error) {
          failed.push(files.length > 1 ? fill(R.SCAN_PHOTO_FAILED, { n: i + 1, name: (file as File).name ?? "" }) + receiptScanner.error : receiptScanner.error);
        }
      }
    } catch (e) {
      receiptScanner.fail(e);
      return;
    } finally {
      receiptScanner.photo = { n: 0, of: 0 };
    }
    receiptScanner.error = failed.join(" ");
  }

  /** One photo: read it, and put every receipt in it on the waiting list (null when none). */
  async function scanOne(file: Blob, groupId: string | null): Promise<ReceiptDraft[] | null> {
    const photo = await receiptScanner.prepare(file);
    if (!photo) return null;
    const shown = await photoToDataUrl(photo).catch(() => "");
    const drafts = await receiptScanner.scan(photo);
    if (!drafts.length) {
      receiptScanner.note("Nothing to review");
      return null;
    }
    if (!groupId) {
      receiptScanner.note("No group was open, so there's nowhere to add it");
      return null;
    }
    receiptScanner.keep(groupId, drafts, shown);
    if (!receiptScanner.screens || app.groupId !== groupId) {
      receiptScanner.note("The Receipts page was closed when the AI finished; the review opens when you go back");
      for (const d of drafts) if (shows(R.SCAN_READY_TOAST)) app.toast(fill(R.SCAN_READY_TOAST, { store: d.store }));
    }
    return drafts;
  }

  /** Puts readings (from the test bench) on the waiting list, like a fresh scan. */
  export async function queue(drafts: ReceiptDraft[], photo: Blob) {
    if (!app.groupId || !drafts.length) return;
    receiptScanner.keep(app.groupId, drafts, await photoToDataUrl(photo).catch(() => ""));
  }

  /** Fills the form from a receipt waiting to be checked. */
  function open(d: ReceiptDraft) {
    const known = Object.keys(S.stores).find((n) => n.toLowerCase() === d.store.toLowerCase());
    store = known ?? (d.store === d.store.toUpperCase() ? tidyName(d.store) : d.store);
    date = d.date ?? today();
    card = (d.cardLast4 && moneyCards.with(S.settings.cards).find((c) => c.includes(d.cardLast4!))) || "";
    total = d.totalCents === null ? "" : centsInput(d.totalCents);
    receipt = nextReceiptLabel(S, date, store);
    rows = d.items.map((i) => ({
      keep: true,
      itemNo: i.itemNo,
      name: i.name,
      price: centsInput(i.priceCents),
      coupon: i.couponCents ? centsInput(Math.abs(i.couponCents)) : "",
      crv: i.crvCents ? centsInput(i.crvCents) : "",
      taxed: i.taxable === true ? "yes" : i.taxable === false ? "no" : "",
    }));
  }

  /** Copies the prompt for any AI chat; where the browser won't copy, shows it to copy by hand. */
  async function copyPrompt() {
    try {
      await navigator.clipboard.writeText(RECEIPT_CHAT_PROMPT);
      app.toast(R.PASTE_PROMPT_COPIED);
    } catch {
      pasting = true;
      pasteText = RECEIPT_CHAT_PROMPT;
      app.toast(R.PASTE_PROMPT_COPY_FAILED);
    }
  }

  /** Reads an AI chat's reply and puts each receipt in it on the waiting list, like a scan. */
  function readPaste() {
    pasteError = "";
    const drafts = pastedReceipts(pasteText);
    if (!drafts.length || !app.groupId) {
      pasteError = R.PASTE_NOTHING;
      return;
    }
    receiptScanner.keep(app.groupId, drafts, "", true);
    pasteText = "";
    pasting = false;
    app.toast(fill(R.PASTE_READ_TOAST, { n: drafts.length, receipts: drafts.length === 1 ? "receipt" : "receipts" }));
  }

  async function copyLog() {
    try {
      await navigator.clipboard.writeText(receiptScanner.log.join("\n"));
      app.toast(R.SCAN_LOG_COPIED);
    } catch (e) {
      receiptScanner.note(`Couldn't copy: ${(e as Error).message}`);
    }
  }

  // The first receipt waiting in this group opens here, also after leaving the page or reloading.
  let shownFor = "";
  $effect(() => {
    const c = current;
    if (!c || c.id === shownFor) return;
    shownFor = c.id;
    untrack(() => {
      open(c.draft);
      receiptScanner.note(`Opened the review: ${rows.length} rows`);
    });
  });
  onMount(() => receiptScanner.screens++);
  onDestroy(() => receiptScanner.screens--);

  /** Done with the open receipt (added or cancelled): the next one waiting opens. */
  function next() {
    if (current) receiptScanner.drop(current.id);
  }

  function cancelAll() {
    const n = waiting.length;
    if (app.groupId) receiptScanner.dropAll(app.groupId);
    app.toast(fill(R.SCAN_CANCELLED_ALL, { n }));
  }

  async function add() {
    if (!draft || !store.trim()) return;
    saving = true;
    try {
      const plan = receiptAddCommands(
        S,
        {
          draft,
          store,
          date,
          receipt,
          card,
          totalCents: readMoney(total),
          rows: kept.map((r) => ({
            itemNo: r.itemNo,
            name: r.name,
            priceCents: readMoney(r.price),
            couponCents: readMoney(r.coupon) ?? 0,
            crvCents: readMoney(r.crv) ?? 0,
            taxed: r.taxed === "yes" ? true : r.taxed === "no" ? false : null,
          })),
          ticked,
        },
        () => crypto.randomUUID(),
      );
      let n = 0;
      for (const cmd of plan.commands) {
        const err = await app.do_(cmd);
        if (err) {
          receiptScanner.note(`${cmd.type} was refused: ${err}`);
          break;
        }
        if (cmd.type === "item.add") n++;
      }
      receiptScanner.note(`Added ${n} of ${kept.length} rows`);
      app.toast(fill(R.SCAN_ADDED_TOAST, { n, items: n === 1 ? "item" : "items" }));
      next();
    } catch (e) {
      receiptScanner.fail(e);
    } finally {
      saving = false;
    }
  }
</script>

<div class="scan">
  <div class="scan-head">
    <div>
      {#if shows(R.SCAN_TITLE)}<strong>{R.SCAN_TITLE}</strong>{/if}
      {#if shows(H.RECEIPT_SCAN_NOTE)}<p class="muted small">{H.RECEIPT_SCAN_NOTE}</p>{/if}
    </div>
    <div class="actions">
      {#if receiptScanner.photo.of > 1 && shows(R.SCAN_PHOTO_PROGRESS)}
        <span class="small">{fill(R.SCAN_PHOTO_PROGRESS, receiptScanner.photo)}</span>
      {/if}
      {#if receiptScanner.busy}
        {#if shows(R.SCAN_READING_TEXT)}<span class="small">{R.SCAN_READING_TEXT}</span>{/if}
        <button class="btn small" onclick={() => receiptScanner.stop()}>{R.SCAN_STOP}</button>
      {:else if receiptScanner.stage === "opening" || receiptScanner.stage === "waiting"}
        {@const words = receiptScanner.stage === "opening" ? R.SCAN_OPENING_TEXT : R.SCAN_WAITING_TEXT}
        {#if shows(words)}<span class="small">{words}</span>{/if}
      {:else if !receiptScanner.photo.of}
        <button class="btn small primary" disabled={app.readOnly} onclick={() => fileInput?.click()}>{R.SCAN_BUTTON}</button>
      {/if}
      {#if shows(R.PASTE_COPY_PROMPT_BUTTON)}
        <button class="btn small" disabled={app.readOnly} onclick={copyPrompt}>{R.PASTE_COPY_PROMPT_BUTTON}</button>
      {/if}
      {#if shows(R.PASTE_BUTTON)}
        <button class="btn small" disabled={app.readOnly} aria-expanded={pasting} onclick={() => ((pasting = !pasting), (pasteError = ""))}>{R.PASTE_BUTTON}</button>
      {/if}
      <input bind:this={fileInput} type="file" accept="image/*" multiple hidden onchange={pick} aria-label={R.SCAN_PICKER_LABEL} />
    </div>
  </div>
  {#if pasting}
    <div class="paste">
      <textarea class="input" rows="6" bind:value={pasteText} placeholder={R.PASTE_PLACEHOLDER} aria-label={R.PASTE_LABEL}></textarea>
      {#if pasteError}<p class="small owes">{pasteError}</p>{/if}
      <div class="btns">
        <button class="btn small primary" disabled={!pasteText.trim()} onclick={readPaste}>{R.PASTE_READ_BUTTON}</button>
        <button class="btn small quiet" onclick={() => ((pasting = false), (pasteError = ""))}>{R.PASTE_CLOSE_BUTTON}</button>
      </div>
    </div>
  {/if}
  {#if receiptScanner.error}<p class="small owes">{receiptScanner.error}</p>{/if}
  {#if shows(R.SCAN_LOG_TITLE) && receiptScanner.log.length}
    <details class="small scan-log">
      <summary>{R.SCAN_LOG_TITLE}</summary>
      <button class="btn small" onclick={copyLog}>{R.SCAN_LOG_COPY}</button>
      <pre>{receiptScanner.log.join("\n")}</pre>
    </details>
  {/if}
  {#if receiptScanner.busy && receiptScanner.thinking && shows(R.SCAN_THINKING_TITLE)}
    <details class="small"><summary>{R.SCAN_THINKING_TITLE}</summary><pre>{receiptScanner.thinking}</pre></details>
  {/if}

  {#if current && draft}
    {#if waiting.length > 1}
      <div class="queue">
        {#if shows(R.SCAN_QUEUE_TEXT)}<span class="small">{fill(R.SCAN_QUEUE_TEXT, { n: waiting.length })}</span>{/if}
        <ol>
          {#each waiting as w, i (w.id)}
            <li class="chip" class:on={i === 0}>{fill(R.SCAN_QUEUE_ITEM, { store: w.draft.store, date: w.draft.date ? shortDate(w.draft.date) : "", n: w.draft.items.length, rows: w.draft.items.length === 1 ? "row" : "rows" })}</li>
          {/each}
        </ol>
        <button class="btn small quiet" onclick={cancelAll}>{R.SCAN_CANCEL_ALL}</button>
      </div>
    {/if}
    <div class="review" class:bare={!current.photo}>
      <div class="side">
        {#if current.pasted}
          {#if shows(R.PASTE_SOURCE_TEXT)}<span class="small chip">{R.PASTE_SOURCE_TEXT}</span>{/if}
        {:else if current.of > 1 && shows(R.SCAN_PLACE_TEXT)}<span class="small">{fill(R.SCAN_PLACE_TEXT, { place: current.place, of: current.of })}</span>{/if}
        {#if current.photo}<img src={current.photo} alt={R.SCAN_PHOTO_ALT} class="photo" />{/if}
      </div>
      <div class="form">
        <div class="meta">
          <label class="field"><span>{R.SCAN_STORE_LABEL}</span><input class="input" bind:value={store} /></label>
          <label class="field"><span>{R.SCAN_DATE_LABEL}</span><input class="input" type="date" bind:value={date} /></label>
          <label class="field"><span>{R.SCAN_RECEIPT_LABEL}</span><input class="input" bind:value={receipt} placeholder={R.SCAN_RECEIPT_PLACEHOLDER} /></label>
          <div class="field"><span>{R.SCAN_CARD_LABEL}</span><CardPicker bind:value={card} cards={moneyCards.with(S.settings.cards)} label={R.SCAN_CARD_LABEL} /></div>
          <label class="field"><span>{R.SCAN_TOTAL_LABEL}</span><input class="input money" inputmode="decimal" bind:value={total} /></label>
        </div>
        <div class="rows">
          <div class="r head" aria-hidden="true"><span></span><span>{R.SCAN_COL_ITEM_NO}</span><span>{R.SCAN_COL_NAME}</span><span>{R.SCAN_COL_PRICE}</span><span>{R.SCAN_COL_COUPON}</span><span>{R.SCAN_COL_CRV}</span><span>{R.SCAN_COL_TAX}</span></div>
          {#each rows as r, i (i)}
            <div class="r" class:off={!r.keep}>
              <input type="checkbox" bind:checked={r.keep} aria-label={fill(R.SCAN_ROW_KEEP_LABEL, { name: r.name })} />
              <input class="input" bind:value={r.itemNo} aria-label={R.SCAN_ROW_ITEM_NO_LABEL} />
              <input class="input" bind:value={r.name} aria-label={R.SCAN_COL_NAME} />
              <input class="input money" inputmode="decimal" bind:value={r.price} aria-label={R.SCAN_COL_PRICE} />
              <input class="input money" inputmode="decimal" bind:value={r.coupon} placeholder="0" aria-label={R.SCAN_COL_COUPON} />
              <input class="input money" inputmode="decimal" bind:value={r.crv} placeholder="0" aria-label={R.SCAN_COL_CRV} />
              <select class="input" bind:value={r.taxed} aria-label={R.SCAN_ROW_TAX_LABEL}>
                <option value="">{R.SCAN_TAX_PRESET}</option>
                <option value="yes">{R.SCAN_TAX_YES}</option>
                <option value="no">{R.SCAN_TAX_NO}</option>
              </select>
            </div>
          {/each}
        </div>
        <p class="small" class:owes={printed !== null && printed !== rowsCents}>
          {fill(R.SCAN_ROWS_SUM, { n: kept.length, items: kept.length === 1 ? "item" : "items", cents: formatCents(rowsCents) })}{#if printed !== null}{fill(R.SCAN_SUBTOTAL, { cents: formatCents(printed) })}{printed === rowsCents ? R.SCAN_SUBTOTAL_MATCHES : ""}{/if}.
          {#if draftSubtotal(draft) !== rowsCents && shows(R.SCAN_ROWS_CHANGED)}<span class="muted">{R.SCAN_ROWS_CHANGED}</span>{/if}
        </p>
        <div class="who">
          {#if shows(R.SCAN_SPLIT_LABEL)}<span class="small">{R.SCAN_SPLIT_LABEL}</span>{/if}
          {#each people as p (p.id)}
            {@const on = ticked.includes(p.id)}
            <button type="button" class="chip" class:on onclick={() => (ticked = on ? ticked.filter((x) => x !== p.id) : [...ticked, p.id])}>{p.name}</button>
          {/each}
          {#if shows(H.SCAN_SPLIT_NOTE)}<span class="muted small">{H.SCAN_SPLIT_NOTE}</span>{/if}
        </div>
        <div class="btns">
          <button class="btn primary" disabled={saving || !kept.length || !store.trim()} onclick={add}>{fill(R.SCAN_ADD_BUTTON, { n: kept.length, items: kept.length === 1 ? "item" : "items" })}</button>
          <button class="btn quiet" onclick={next}>{R.SCAN_CANCEL}</button>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .scan {
    margin-bottom: 18px;
    padding: 14px 16px;
    border-radius: var(--r-lg);
    background: color-mix(in srgb, #2f8fd6 8%, var(--surface));
    border: 1px solid color-mix(in srgb, #2f8fd6 25%, transparent);
  }
  .scan-head {
    display: flex;
    gap: 16px;
    justify-content: space-between;
    flex-wrap: wrap;
  }
  .scan-head > div:first-child {
    flex: 1 1 320px;
  }
  .scan p {
    margin: 4px 0 0;
  }
  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }
  .small {
    font-size: var(--t-xs);
  }
  pre {
    white-space: pre-wrap;
    max-height: 160px;
    overflow: auto;
  }
  .review {
    display: grid;
    grid-template-columns: minmax(0, 220px) 1fr;
    gap: 16px;
    margin-top: 14px;
  }
  .review.bare {
    grid-template-columns: 1fr;
  }
  .side .chip {
    justify-self: start;
    cursor: default;
  }
  .paste {
    display: grid;
    gap: 8px;
    margin-top: 12px;
  }
  .paste textarea {
    width: 100%;
    font-family: var(--mono);
    font-size: var(--t-xs);
    resize: vertical;
  }
  .queue {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    margin-top: 14px;
  }
  .queue ol {
    display: contents;
    list-style: none;
  }
  .side {
    display: grid;
    gap: 6px;
    align-content: start;
    min-width: 0;
  }
  .photo {
    width: 100%;
    max-height: 520px;
    object-fit: contain;
    border-radius: var(--r-md, 8px);
    background: var(--surface);
  }
  .form {
    display: grid;
    gap: 10px;
    min-width: 0;
  }
  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .meta .field {
    flex: 1 1 130px;
  }
  .rows {
    display: grid;
    gap: 4px;
    max-height: 420px;
    overflow: auto;
  }
  .r {
    display: grid;
    grid-template-columns: 24px 90px minmax(120px, 1fr) 80px 72px 64px 96px;
    gap: 6px;
    align-items: center;
  }
  .r.head {
    font-size: var(--t-xs);
    font-weight: 600;
    color: var(--muted);
  }
  .r .input {
    padding: 4px 8px;
    min-height: 30px;
    font-size: var(--t-xs);
  }
  .r.off {
    opacity: 0.45;
  }
  .who {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
  }
  .chip {
    padding: 3px 10px;
    border-radius: 999px;
    border: 1px solid var(--line, #ccc);
    background: var(--surface);
    font-size: var(--t-xs);
    cursor: pointer;
  }
  .chip.on {
    background: var(--accent, #2f8fd6);
    border-color: transparent;
    color: white;
  }
  .btns {
    display: flex;
    gap: 8px;
  }
  @media (max-width: 720px) {
    .review {
      grid-template-columns: 1fr;
    }
    .photo {
      max-height: 240px;
    }
    .r {
      grid-template-columns: 24px 1fr 70px 64px;
    }
    .r > :nth-child(2),
    .r > :nth-child(6),
    .r > :nth-child(7) {
      display: none;
    }
  }
</style>
